import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import io from "socket.io-client";
import { successMessages } from "../store/actionCreators/messages";
import {
  getSkinId,
  getToken,
  getUserId,
  getUsername,
} from "../store/selectors";

const socketUrl = process.env.REACT_APP_SOCKET_URL || "";

const useMessagesSocket = () => {
  const dispatch = useDispatch();
  const socket: any = useRef(null);
  const token = useSelector(getToken);
  const username = useSelector(getUsername);
  const userId = useSelector(getUserId);
  const skinId = useSelector(getSkinId);

  // Use refs to store the latest handlers to avoid stale closures
  const onNewMessageRef = useRef<(message: { status: string; templateId?: string }) => void>();

  // Update the ref whenever dependencies change
  useEffect(() => {
    onNewMessageRef.current = (message: { status: string; templateId?: string }) => {
      if (!message) return;
      message.status = "unread";
      dispatch(successMessages([message] || []));
    };
  }, [dispatch]);

  useEffect(() => {
    if (!socketUrl || !userId || !username || !token) return;

    socket.current = io(socketUrl, {
      query: { username, id: userId, token },
    });

    // Clean up previous listeners before adding new ones
    const socketInstance = socket.current;

    const handleNewMessage = (message: { status: string }) => {
      onNewMessageRef.current?.(message);
    };

    socketInstance.on("newMessage", handleNewMessage);
    socketInstance.on("disconnect", () => {
      console.error("My socket disconnected gameBox :(");
    });

    return () => {
      // Remove the specific event listener first
      socketInstance.off("newMessage", handleNewMessage);
      socketInstance.disconnect(true);
      socket.current = null;
    };
  }, [userId, username, token]); // Remove onNewMessage from dependencies

  useEffect(() => {
    if (skinId && socket.current) {
      socket.current.emit(
          "getMessages",
          { skinId },
          (
              err: any,
              params: { payload: { messages: { [x: string]: any[] } } }
          ) => {
            if (err) {
              return;
            }
            dispatch(successMessages(params.payload.messages[userId] || []));
          }
      );
    }
  }, [skinId, userId, dispatch]); // Remove socket.current from dependencies

  return useCallback((type: any, params: any) => {
    if (socket.current) socket.current.emit(type, params);
  }, []);
};

export default useMessagesSocket;